import { ref, watch } from 'vue'
import { listConversations, type ConversationListItem } from '@/services/wootdashConversations'
import { onListChanged, onRealtimeRecovered } from '@/composables/useConversationRealtime'
import { hiddenFluxoIdsCp, isFluxoHiddenCp } from '@/composables/useHiddenFluxosCp'

// Motor leve e independente de página — o rail do ConvDrawer.vue (montado globalmente em
// App.vue) precisa saber "minhas conversas ativas" mesmo com o agente em outra tela (ex.:
// Templates), não só quando o Dashboard está montado. Substitui, pro que é WootDash, o
// papel que useDashboardEngine.ts (Chatwoot) cumpria — aquele não é tocado, continua servindo
// FocoView.vue/MobileView.vue sem mudança.
// Status abertos pro rail pessoal — espelha o filtro `statuses: ['Open', 'Pending']`
// do refetch abaixo (enum numérico do backend: Open=0, Pending=1).
const ACTIVE_STATUSES = new Set([0, 1])

const myActiveConversations = ref<ConversationListItem[]>([])
let started = false
let unsubscribeListChanged: (() => void) | null = null
let stopRecovered: (() => void) | null = null
let currentToken = ''
let currentAgentId = ''

async function refetch() {
  if (!currentToken) return
  const { conversations } = await listConversations(currentToken, {
    onlyMine: true,
    statuses: ['Open', 'Pending'],
    pageSize: 100,
    // "Esconder Fluxo" (DashHeader.vue) vale também no Foco — mesmo filtro do Dashboard.
    excludeFlowIds: hiddenFluxoIdsCp.value,
  })
  myActiveConversations.value = conversations
}

// Reconcilia uma única conversa a partir do payload de "ListaDeConversasAlterada" — evita
// o refetch completo (10 requisições HTTP/agente por evento, medido antes desta fase; ver
// plano-performance-escalabilidade.md item 3). Entra/atualiza a conversa se ela ainda é
// minha e está aberta/pendente; sai da lista se deixou de ser (resolvida, fechada ou
// reatribuída a outro agente).
function applyListItem(item: ConversationListItem): void {
  const belongs = item.agentId === currentAgentId && ACTIVE_STATUSES.has(item.status) && !isFluxoHiddenCp(item.flowId)
  const idx = myActiveConversations.value.findIndex(c => c.id === item.id)
  if (!belongs) {
    if (idx !== -1) myActiveConversations.value.splice(idx, 1)
    return
  }
  if (idx !== -1) myActiveConversations.value.splice(idx, 1, item)
  else myActiveConversations.value = [item, ...myActiveConversations.value]
}

// Trocar "Esconder Fluxo" refaz a busca na hora, sem esperar o próximo evento realtime/poll.
watch(hiddenFluxoIdsCp, () => { if (started) refetch() })

export function useMyActiveConversations() {
  return { myActiveConversations }
}

/** Idempotente — chamar de novo com o motor já rodando só atualiza token/agentId. */
export function startMyActiveConversations(token: string, agentId = ''): void {
  currentToken = token
  currentAgentId = agentId
  if (started) return
  started = true
  refetch()
  unsubscribeListChanged = onListChanged(applyListItem)
  // Sem polling: o merge incremental cobre o dia a dia, e o que se perder numa queda da conexão é rebuscado
  // quando ela volta (onRealtimeRecovered) — substitui o refetch a cada 60 s que existia só pra cobrir isso.
  stopRecovered = onRealtimeRecovered(() => refetch())
}

export function stopMyActiveConversations(): void {
  started = false
  currentToken = ''
  currentAgentId = ''
  myActiveConversations.value = []
  unsubscribeListChanged?.()
  unsubscribeListChanged = null
  stopRecovered?.()
  stopRecovered = null
}
